import React, { useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  BarChart2,
  Bell,
  BookOpen,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  Compass,
  Grid as GridIcon,
  HelpCircle,
  Layout,
  Layers,
  LogOut,
  Menu,
  Search,
  Settings,
  Users,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import NotificationBell from './NotificationBell';

function navActive(pathname, href) {
  if (href === '/dashboard') return pathname === '/dashboard';
  return pathname === href || pathname.startsWith(`${href}/`);
}

function SidebarLink({ to, icon: Icon, children, pathname }) {
  return (
    <Link to={to} className={`sidebar-nav-link${navActive(pathname, to) ? ' active' : ''}`}>
      <Icon size={20} /> <span>{children}</span>
    </Link>
  );
}

function DashboardLayout({ children, showTopBar = true }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  const isAdmin = useMemo(
    () =>
      user?.is_staff ||
      user?.is_superuser ||
      ['admin', 'campus_admin', 'finance', 'superuser'].includes(user?.user_type),
    [user]
  );

  const isFinanceOnly = useMemo(
    () => user?.user_type === 'finance' && !user?.is_staff && !user?.is_superuser,
    [user]
  );

  const handleLogout = async () => {
    await logout();
    toast.success('Logged out successfully');
    navigate('/auth/login');
  };

  return (
    <div className="dashboard-layout">
      {isSidebarOpen && (
        <div className="sidebar-overlay" onClick={() => setIsSidebarOpen(false)} />
      )}
      <aside
        className={`dashboard-sidebar ${isSidebarOpen ? 'open' : ''} ${isSidebarCollapsed ? 'collapsed' : ''}`}
      >
        <div className="sidebar-header">
          <div className="sidebar-logo" style={{ marginBottom: 0 }}>
            {isSidebarCollapsed ? 'L' : 'Learnly'}
          </div>
          <button
            onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--color-text-secondary)',
              padding: '4px',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            title={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            type="button"
          >
            {isSidebarCollapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
          </button>
        </div>

        <div className="sidebar-user">
          <div className="user-avatar">{user?.first_name?.[0] || user?.username?.[0] || 'U'}</div>
          <div className="overflow-hidden">
            <div className="font-bold text-sm truncate">
              {user?.first_name} {user?.last_name}
            </div>
            <div className="text-xs truncate">{user?.email}</div>
          </div>
        </div>

        <div className="sidebar-scroll">
        <div className="sidebar-nav-section">
          <div className="sidebar-nav-title">
            <span>Main Menu</span>
          </div>
          {isAdmin ? (
            <>
              <SidebarLink to="/dashboard" icon={GridIcon} pathname={pathname}>
                Admin Dashboard
              </SidebarLink>
              {isFinanceOnly ? (
                <SidebarLink to="/admin/enrollments/payment-verification" icon={Layout} pathname={pathname}>
                  Manage Enrollments
                </SidebarLink>
              ) : (
                <>
                  <SidebarLink to="/admin/students" icon={Users} pathname={pathname}>
                    Manage Students
                  </SidebarLink>
                  <SidebarLink to="/admin/enrollments" icon={Layout} pathname={pathname}>
                    Manage Enrollments
                  </SidebarLink>
                  <SidebarLink to="/admin/cohorts" icon={Layers} pathname={pathname}>
                    Manage Cohorts
                  </SidebarLink>
                  <SidebarLink to="/admin/courses" icon={BookOpen} pathname={pathname}>
                    Manage Courses
                  </SidebarLink>
                  <SidebarLink to="/admin/assessments" icon={ClipboardCheck} pathname={pathname}>
                    Manage Assessments
                  </SidebarLink>
                </>
              )}
              <SidebarLink to="/admin/notifications" icon={Bell} pathname={pathname}>
                Notifications
              </SidebarLink>
              {!isFinanceOnly && (
                <SidebarLink to="/admin/reports" icon={BarChart2} pathname={pathname}>
                  Reporting Center
                </SidebarLink>
              )}
            </>
          ) : (
            <>
              <SidebarLink to="/dashboard" icon={GridIcon} pathname={pathname}>
                Overview
              </SidebarLink>
              <SidebarLink to="/courses" icon={Compass} pathname={pathname}>
                Explore Courses
              </SidebarLink>
              <SidebarLink to="/assessments" icon={ClipboardCheck} pathname={pathname}>
                Assessments
              </SidebarLink>
              <SidebarLink to="/enrollments" icon={Layout} pathname={pathname}>
                My Enrollments
              </SidebarLink>
              <SidebarLink to="/notifications" icon={Bell} pathname={pathname}>
                Notifications
              </SidebarLink>
              <SidebarLink to="/analytics" icon={BarChart2} pathname={pathname}>
                My Analytics
              </SidebarLink>
            </>
          )}
        </div>

        {isAdmin && (
          <div className="sidebar-nav-section">
            <div className="sidebar-nav-title">
              <span>Labels</span>
            </div>
            <div className="sidebar-nav-link">
              <div
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: 'var(--color-accent-peach)', flexShrink: 0 }}
              />
              <span>UI Design</span>
            </div>
            <div className="sidebar-nav-link">
              <div
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: 'var(--color-accent-blue)', flexShrink: 0 }}
              />
              <span>UX Design</span>
            </div>
          </div>
        )}

        <div className="bottom-nav-section">
          <SidebarLink to="/profile" icon={Settings} pathname={pathname}>
            System Settings
          </SidebarLink>
          <Link to="/help" className={`sidebar-nav-link${navActive(pathname, '/help') ? ' active' : ''}`}>
            <HelpCircle size={20} /> <span>Help Center</span>
          </Link>
          <button
            onClick={handleLogout}
            className="sidebar-nav-link w-full text-left bg-transparent border-none cursor-pointer"
            style={{ background: 'transparent' }}
            type="button"
          >
            <LogOut size={20} /> <span>Logout Account</span>
          </button>
        </div>
        </div>
      </aside>

      <main className="dashboard-main">
        <div className="dashboard-content-wrapper">
          {showTopBar && (
            <header className="dashboard-top-bar">
              <div className="flex items-center gap-md">
                <button
                  className="mobile-menu-toggle"
                  onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                  aria-label="Toggle menu"
                  type="button"
                >
                  <Menu size={24} />
                </button>
                <div className="search-container">
                  <Search className="search-icon" size={18} />
                  <input type="text" placeholder="Search..." />
                </div>
              </div>

              <div className="flex items-center gap-lg">
                <NotificationBell variant="dashboard" inboxPath="/notifications" />
                <button className="btn btn-primary px-xl" type="button">
                  See Tutorial
                </button>
              </div>
            </header>
          )}

          <div className="dashboard-content">{children}</div>
        </div>
      </main>
    </div>
  );
}

export default DashboardLayout;
